import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useAdminShortcuts — keyboard navigation across the admin surface.
 *
 * Gmail-style "press g then <key>" jumps to the matching page. `?` shows
 * a cheat sheet overlay so power users can learn the bindings without
 * leaving the keyboard.
 *
 * Bindings:
 *   g d   → /admin             (Dashboard)
 *   g l   → /admin/leads       (Leads inbox)
 *   g p   → /admin/pages       (Pages CMS)
 *   g s   → /admin/seo         (SEO hub)
 *   g b   → /admin/blog        (Blog)
 *   g r   → /admin/resources   (Resources)
 *   g o   → /admin/orders      (Number orders)
 *   g n   → /admin/partners    (Partner apps)
 *   g a   → /admin/ai          (AI prompts)
 *   g t   → /admin/settings    (Settings)
 *   ?     → show cheat sheet
 *   esc   → hide cheat sheet
 *
 * Shortcuts are disabled while the user is typing in an input / textarea
 * / contenteditable element so they don't fight with editing.
 */
const ROUTES = {
  d: '/admin',
  l: '/admin/leads',
  p: '/admin/pages',
  s: '/admin/seo',
  b: '/admin/blog',
  r: '/admin/resources',
  o: '/admin/orders',
  n: '/admin/partners',
  a: '/admin/ai',
  t: '/admin/settings',
  u: '/admin/audit-log',
};

export default function useAdminShortcuts() {
  const navigate = useNavigate();
  const [cheatOpen, setCheatOpen] = useState(false);

  useEffect(() => {
    let waitingG = false;
    let gTimer = null;

    const isTyping = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const handler = (e) => {
      // Don't interrupt typing.
      if (isTyping(document.activeElement)) return;
      // Ignore when a modifier (cmd/ctrl) is held — those are real shortcuts.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const k = e.key.toLowerCase();

      if (k === '?') {
        setCheatOpen((v) => !v);
        e.preventDefault();
        return;
      }
      if (k === 'escape' && cheatOpen) {
        setCheatOpen(false);
        return;
      }

      if (waitingG && ROUTES[k]) {
        navigate(ROUTES[k]);
        waitingG = false;
        if (gTimer) clearTimeout(gTimer);
        e.preventDefault();
        return;
      }

      if (k === 'g') {
        waitingG = true;
        if (gTimer) clearTimeout(gTimer);
        gTimer = setTimeout(() => { waitingG = false; }, 1200);
      } else {
        waitingG = false;
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (gTimer) clearTimeout(gTimer);
    };
  }, [navigate, cheatOpen]);

  return { cheatOpen, setCheatOpen };
}

export const SHORTCUT_LIST = [
  { keys: 'g d', label: 'Dashboard' },
  { keys: 'g l', label: 'Leads' },
  { keys: 'g p', label: 'Pages' },
  { keys: 'g s', label: 'SEO' },
  { keys: 'g b', label: 'Blog' },
  { keys: 'g r', label: 'Resources' },
  { keys: 'g o', label: 'Number orders' },
  { keys: 'g n', label: 'Partner apps' },
  { keys: 'g a', label: 'AI prompts' },
  { keys: 'g t', label: 'Settings' },
  { keys: 'g u', label: 'Audit log' },
  { keys: '?',   label: 'Toggle this cheat sheet' },
];
